(function (global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  global.NewHomeSource.Carousel = global.NewHomeSource.Carousel || {};

  const Carousel = global.NewHomeSource.Carousel;

  if (!global.NewHomeSource.Helpers ||
    !global.NewHomeSource.Helpers.Array ||
    !Carousel.LoopPoint) {
    return;
  }

  const arrayHelper = global.NewHomeSource.Helpers.Array;

  class SlideLooper {
    /**
     * Initializes a new instance of the class.
     * Sets the rounding safety margin and initializes the state.
     *
     * @constructor
     * @property {number} roundingSafety - The safety margin used for rounding operations.
     * @property {Object} state - The current state of the carousel, initially set to null.
     */
    constructor() {
      this.roundingSafety = 0.5;
      this.state = null;
    }

    /**
     * Initializes the slideLooper with the given state.
     * Sets up the slide sizes, ascending and descending item arrays, and loop points.
     *
     * @param {Object} state - The state object containing configuration and data for the slideLooper.
     */
    initialize(state) {
      this.state = state;
      const slideSizesWithGaps = this.state.get('slideSizesWithGaps');
      this.ascItems = arrayHelper.arrayKeys(slideSizesWithGaps);
      this.descItems = this.ascItems.slice().reverse();
      this.loopPoints = this.startPoints().concat(this.endPoints());
    }

    /**
     * Removes the sizes of specified slides from a starting value.
     *
     * @param {number[]} indexes - Array of slide indexes whose sizes should be subtracted.
     * @param {number} from - The initial value to subtract slide sizes from.
     * @returns {number} The resulting value after subtracting the sizes of the specified slides.
     */
    removeSlideSizes(indexes, from) {
      const slideSizesWithGaps = this.state.get('slideSizesWithGaps');
      return indexes.reduce((a, i) => a - slideSizesWithGaps[i], from);
    }

    /**
     * Returns an array of slide indexes that fit within the specified gap.
     *
     * Iterates through the provided indexes, adding each index to the result array
     * as long as the total size of the selected slides does not exceed the given gap.
     * The size of each slide is determined by the `removeSlideSizes` method.
     *
     * @param {number[]} indexes - An array of slide indexes to consider.
     * @param {number} gap - The maximum allowed total size for the selected slides.
     * @returns {number[]} An array of slide indexes that fit within the gap.
     */
    slidesInGap(indexes, gap) {
      return indexes.reduce((a, i) => {
        const remainingGap = this.removeSlideSizes(a, gap);
        return remainingGap > 0 ? a.concat([i]) : a;
      }, []);
    }

    /**
     * Calculates the start and end bounds for each slide based on the current offset.
     *
     * @param {number} offset - The offset to apply to each slide's bounds.
     * @returns {Array<{start: number, end: number}>} An array of objects representing the start
     * and end bounds for each slide.
     */
    findSlideBounds(offset) {
      const slideSizes = this.state.get('slideSizes');
      const viewSize = this.state.get('viewSize');
      const snaps = this.state.get('snaps');

      return snaps.map((snap, index) => ({
        start: snap - slideSizes[index] + this.roundingSafety + offset,
        end: snap + viewSize - this.roundingSafety + offset
      }));
    }

    /**
     * Calculates loop points for a set of slide indexes in a carousel, determining where each
     * slide should appear when looping.
     *
     * @param {number[]} indexes - Array of slide indexes to calculate loop points for.
     * @param {number} offset - The offset used to find slide bounds.
     * @param {boolean} isEndEdge - Indicates if the calculation is for the end edge (true) or
     * start edge (false) of the carousel.
     * @returns {Array<Object>} An array of objects for each index containing:
     *   - {number} index: The slide index.
     *   - {number} loopPoint: The calculated loop point for the slide.
     *   - {Carousel.Vector1D} slideLocation: The initialized slide location vector.
     *   - {Function} translate: Function to translate the slide along the carousel axis.
     *   - {Function} target: Function returning the target position based on the current location.
     */
    findLoopPoints(indexes, offset, isEndEdge) {
      const slideBounds = this.findSlideBounds(offset);
      return indexes.map((index) => {
        const boundEdge = isEndEdge ? 'end' : 'start';
        const loopPointOptions = {
          loopPoint: slideBounds[index][boundEdge],
          index,
          isEndEdge
        };
        const loopPoint = new Carousel.LoopPoint();
        loopPoint.initialize(this.state, loopPointOptions);
        return loopPoint;
      });
    }

    /**
     * Calculates and returns the loop points for the carousel slides.
     *
     * Retrieves scroll snap positions and content size from the state,
     * determines the gap between slides, computes the relevant slide indexes,
     * and finds the loop points for seamless carousel looping.
     *
     * @returns {Array} An array of loop point objects or indexes for the carousel.
     */
    startPoints() {
      const scrollSnaps = this.state.get('scrollSnaps');
      const startEdgeOffset = this.state.get('contentSize');
      const gap = scrollSnaps[0];
      const indexes = this.slidesInGap(this.descItems, gap);
      const isEndEdge = false;
      return this.findLoopPoints(indexes, startEdgeOffset, isEndEdge);
    }

    /**
     * Calculates and returns the loop points for the carousel based on the current state.
     *
     * Retrieves the view size, scroll snaps, and content size from the state,
     * computes the gap, determines the relevant slide indexes within that gap,
     * and finds the loop points for seamless carousel looping.
     *
     * @returns {Array} An array of loop point objects or indexes for the carousel.
     */
    endPoints() {
      const viewSize = this.state.get('viewSize');
      const scrollSnaps = this.state.get('scrollSnaps');
      const contentSize = this.state.get('contentSize');
      const endEdgeOffset = -contentSize;
      const gap = viewSize - scrollSnaps[0] - 1;
      const isEndEdge = true;
      const indexes = this.slidesInGap(this.ascItems, gap);
      return this.findLoopPoints(indexes, endEdgeOffset, isEndEdge);
    }

    /**
     * Determines if the carousel can loop based on the current view size and loop points.
     * Iterates through each loop point and checks if removing the slide sizes of all other items
     * (excluding the current loop point's index) results in a value less than or equal to 0.1.
     *
     * @returns {boolean} True if the carousel can loop, false otherwise.
     */
    canLoop() {
      const viewSize = this.state.get('viewSize');
      return this.loopPoints.every((loopPoint) => {
        const index = loopPoint.getIndex();
        const otherIndexes = this.ascItems.filter((i) => i !== index);
        return this.removeSlideSizes(otherIndexes, viewSize) <= 0.1;
      });
    }

    /**
     * Iterates over each loop point and updates the slide position if necessary.
     * For each loop point, checks if the current slide location matches the target shift location.
     * If not, translates the slide to the new location and updates the slide location state.
     */
    loop() {
      this.loopPoints.forEach((loopPoint) => {
        const slideLocation = loopPoint.getSlideLocation();
        const shiftLocation = loopPoint.target();
        if (shiftLocation === slideLocation.get()) {
          return;
        }
        loopPoint.getTranslate().to(shiftLocation);
        slideLocation.set(shiftLocation);
      });
    }

    /**
     * Clears the translation state for all loop points in the carousel.
     * Iterates through each loop point and calls its `translate.clear()` method.
     */
    clear() {
      this.loopPoints.forEach((loopPoint) => {
        loopPoint.getTranslate().clear();
      });
    }
  }

  global.NewHomeSource.Carousel.SlideLooper = SlideLooper;
}(window));
